/**
 * patternRegistry.ts — Registro central dos blocos prontos (padrões)
 *
 * Mesma ideia do catálogo de blocos: quem quiser oferecer um bloco pronto
 * registra aqui. A biblioteca lateral do construtor lê SEMPRE deste registro.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, construtor de blocos prontos)
 */

import { getBlock } from '../blockRegistry';
import type { StudioOutput, StudioTemplateType } from '../types';
import type {
  StudioPatternCategory,
  StudioPatternDefinition,
} from './types';

const categorias = new Map<string, StudioPatternCategory>();
const padroes = new Map<string, StudioPatternDefinition>();

/** Registra (ou substitui) uma categoria da biblioteca. */
export function registerPatternCategory(categoria: StudioPatternCategory): void {
  categorias.set(categoria.key, categoria);
}

/** Registra (ou substitui) um bloco pronto. */
export function registerPattern(padrao: StudioPatternDefinition): void {
  padroes.set(padrao.key, padrao);
}

/** Registra vários blocos prontos de uma vez. */
export function registerPatterns(lista: StudioPatternDefinition[]): void {
  lista.forEach(registerPattern);
}

/** Retorna um bloco pronto pela chave. */
export function getPattern(key: string): StudioPatternDefinition | undefined {
  return padroes.get(key);
}

/** Filtros aceitos ao listar padrões. */
export interface StudioPatternFilters {
  /** Tipo do modelo em edição. */
  templateType?: StudioTemplateType;
  /** Formas de entrega do modelo — o padrão precisa atender a TODAS. */
  outputs?: StudioOutput[];
  /** Categoria desejada. */
  category?: string;
  /** Texto digitado na busca. */
  search?: string;
}

/** Verifica se o padrão é compatível com os filtros informados. */
function compativel(padrao: StudioPatternDefinition, filtros: StudioPatternFilters): boolean {
  if (filtros.category && padrao.category !== filtros.category) return false;

  if (filtros.templateType && padrao.templateTypes?.length) {
    if (!padrao.templateTypes.includes(filtros.templateType)) return false;
  }

  if (filtros.outputs?.length) {
    const atende = filtros.outputs.every((saida) => padrao.outputs.includes(saida));
    if (!atende) return false;
  }

  // Segurança: um padrão que use bloco inexistente (ou incompatível com as
  // saídas do modelo) não pode ser oferecido — evitaria conteúdo que some.
  const nos = padrao.build();
  const todosOsBlocos = nos.flatMap(function achatar(no): string[] {
    return [no.type, ...(no.children ?? []).flatMap(achatar)];
  });
  const blocosValidos = todosOsBlocos.every((tipo) => {
    const definicao = getBlock(tipo);
    if (!definicao) return false;
    if (!filtros.outputs?.length) return true;
    return filtros.outputs.every((saida) => definicao.outputs.includes(saida));
  });
  if (!blocosValidos) return false;

  if (filtros.search?.trim()) {
    const termo = filtros.search.trim().toLowerCase();
    const alvo = `${padrao.label} ${padrao.description ?? ''}`.toLowerCase();
    if (!alvo.includes(termo)) return false;
  }

  return true;
}

/** Lista os padrões compatíveis, já ordenados. */
export function listPatterns(filtros: StudioPatternFilters = {}): StudioPatternDefinition[] {
  return Array.from(padroes.values())
    .filter((p) => compativel(p, filtros))
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99) || a.label.localeCompare(b.label, 'pt-BR'));
}

/** Lista as categorias que possuem pelo menos um padrão compatível. */
export function listPatternCategories(
  filtros: StudioPatternFilters = {},
): Array<StudioPatternCategory & { count: number }> {
  return Array.from(categorias.values())
    .map((categoria) => ({
      ...categoria,
      count: listPatterns({ ...filtros, category: categoria.key }).length,
    }))
    .filter((categoria) => categoria.count > 0)
    .sort((a, b) => a.order - b.order);
}
